import { splitPathParts } from './splitPathParts.js';

/**
 * Deeply sets a value in a nested object based on the given path.
 *
 * This function can handle paths that include dot notation for object properties
 * and array index notation. It can also apply a transformation method to the value
 * being set.
 *
 * @param {Object} object - The object in which the value will be set.
 * @param {string} path - A string representing the path to the property where the value should be set.
 *                        The path can include dot notation (e.g., 'foo.bar') and array index notation (e.g., 'foo[0].bar').
 * @param {Function|any} method - Either a value to set or a function to apply to the current value at the specified path.
 *                                If a function is provided, it receives the current value and should return the new value.
 * @returns {Object} A new object with the value set at the specified path.
 *
 * @example
 * const obj = { user: { name: 'John', addresses: [{ id: 'home', city: 'New York' }] } };
 * const updatedObj = setDeep(obj, 'user.age', 30); // { user: { name: 'John', age: 30, addresses: [{ id: 'home', city: 'New York' }] } }
 *
 * const updatedObj2 = setDeep(obj, 'user.addresses[id:home].city', 'Boston'); // { user: { name: 'John', addresses: [{ id: 'home', city: 'Boston' }] } }
 *
 * const updatedObj3 = setDeep(obj, 'user.name', (name) => name.toUpperCase()); // { user: { name: 'JOHN', addresses: [{ id: 'home', city: 'New York' }] } }
 *
 * Update: You can also do user.["user.nested.name"].firstName to access and set keys with dots in them
 */
export const setDeep = (object, path, method) => {
  if (path === '') {
    if (typeof method === 'function') {
      return method(object);
    }
    return method;
  }

  const parts = splitPathParts(path);

  const newObject = {
    ...object
  };
  let runningObject = newObject;
  for (let i = 0; i < parts.length; ++i) {
    const isLast = i === parts.length - 1;
    let key = parts[i];
    let filterParts;
    const matchBrackets = key?.match(/\[.*?\]/g);
    const matchBracketsAndQuotes = key?.match(/\[".*?"\]/g);

    if (matchBrackets && !matchBracketsAndQuotes) {
      const parts = key.split('[');
      key = parts[0];
      filterParts = parts[1].replace(']', '').split(':');
    }

    if (matchBrackets && matchBracketsAndQuotes) {
      key = key.replace('["', '').replace('"]', '');
    }

    // If the path uses bracket syntax (index or filter) but the container doesn't exist yet,
    // initialize it as an array. This prevents cases like `foo[0]` creating `{ foo: { "0": ... } }`.
    if (matchBrackets && !matchBracketsAndQuotes && Array.isArray(filterParts) && filterParts.length > 0) {
      if (runningObject[key] === undefined || runningObject[key] === null) {
        runningObject[key] = [];
      }
    }

    if (Array.isArray(runningObject[key]) && filterParts?.length === 1) {
      // This means we're accessing an array by index
      const filterValue = filterParts[0];
      const match = runningObject[key][filterValue];
      if (isLast) {
        const newValue = typeof method === 'function' ? method(match) : method;
        if (newValue === undefined) {
          runningObject[key].splice(filterValue, 1);
        } else {
          runningObject[key][filterValue] = newValue;
        }
        continue;
      }
      if (match === undefined) {
        const newChild = {};
        runningObject[key][filterValue] = newChild;
        runningObject = newChild;
      } else {
        runningObject = match;
      }
      continue;
    }

    if (Array.isArray(runningObject[key]) && filterParts?.length > 1) {
      const filterKey = filterParts[0];
      const filterValue = filterParts[1];
      const index = runningObject[key].findIndex((n) => n[filterKey] == filterValue); // eslint-disable-line eqeqeq
      if (isLast) {
        const newValue = typeof method === 'function' ? method(runningObject[key][index]) : method;
        if (newValue === undefined) {
          if (index !== -1) {
            runningObject[key].splice(index, 1);
          }
        } else {
          if (index !== -1) {
            runningObject[key][index] = { ...runningObject[key][index], ...newValue };
          } else {
            runningObject[key].push({ [filterKey]: filterValue, ...newValue });
          }
        }
        continue;
      }
      if (index === -1) {
        const newChild = { [filterKey]: filterValue };
        runningObject[key].push(newChild);
        runningObject = newChild;
      } else {
        runningObject = runningObject[key][index];
      }
      continue;
    }
    if (isLast) {
      const newValue = typeof method === 'function' ? method(runningObject[key]) : method;
      if (newValue === undefined) {
        delete runningObject[key];
      } else {
        runningObject[key] = newValue;
      }
      continue;
    }
    runningObject[key] = {
      ...(runningObject[key] || {})
    };
    runningObject = runningObject[key];
  }
  return newObject;
};
